<template>
  <div>
    <p class="cm-mg-p-wzd">
      {{ $t('boxinspection.fifthStepInfo') }}
    </p>
    <hr />
    <div>
      <master-table
        :columns="getClusterTableColumns()"
        :infoTable="getClusterTableData()"
        :indexColumn="{ show: false }"
        :showColumnStatus="false"
        :showColumnImage="false"
        :cellStyle="getAnalyticsCellStyle"
      />
      <br />

      <!-- Mostrar conflictos si los hay -->
      <div v-if="hasConflicts" class="col-12 mb-3">
        <div class="alert alert-warning">
          <strong>{{ $t('common_words.observations') }}:</strong>
          <ul class="mb-0 mt-2">
            <li v-for="(conflict, index) in conflicts" :key="index">
              {{ conflict }}
            </li>
          </ul>
        </div>
      </div>

      <div v-if="hasConflicts" class="col-12 col-md-6 col-lg-3">
        <fg-input
          :label="`${$t('production.rejected_boxes')}: `"
          class="input-box_total_rejection"
          ref="box_total_rejection"
          type="number"
          step="1"
          v-model="model.box_total_rejection"
          :name="`production.rejected_boxes`"
          v-validate="modelValidations.box_total_rejection"
          :error="getError('production.rejected_boxes', true)"
        ></fg-input>
      </div>
    </div>
  </div>
</template>
<script>
import { Axios } from 'src/class/Axios.js';
import MasterTable from 'src/components/Tables/MasterTable.vue';
import MixinForm from 'src/mixins/forms/mixin-form';
import MixinLoading from 'src/mixins/mixin-loading';
import MixinPermissions from 'src/util/mixinPermissions';
import MixinValidations from 'src/mixins/forms/mixin-validations';

export default {
  components: {
    MasterTable,
  },
  data() {
    return {
      model: {
        box_quantity: 0,
        box_total_rejection: 0,
      },
      modelValidations: {
        box_total_rejection: {
          required: false,
          integer: true,
          min_value: 0,
          max_value: 1500,
        },
      },
      qualityPercentages: [],
      compliancePercentage: 0,
      conflicts: [],
      hasConflicts: false,
      isUpdatingConflicts: false,
    };
  },
  mixins: [MixinPermissions, MixinForm, MixinLoading, MixinValidations],
  props: {
    box_quantity: {
      type: Number,
      default: 0,
    },
    compliance_percentage: {
      type: Number,
      default: 0,
    },
    box_selected: {
      type: Object,
      default: null,
    },
    box_weight: {
      type: Array,
      default: () => [],
    },
    box_total_clusters: {
      type: Array,
      default: () => [],
    },
    boxes: {
      type: Object,
      default: () => ({}),
    },
    sample_quantity: {
      type: Array,
      default: () => [],
    },
    defect_box: {
      type: Array,
      default: () => [],
    },
    defect_id: {
      type: Array,
      default: () => [],
    },
    defect_cluster_quantity: {
      type: Array,
      default: () => [],
    },
    defects: {
      type: Array,
      default: () => [],
    },
    date: {
      type: Date,
      default: null,
    },
  },
  watch: {
    box_quantity: {
      handler(newVal) {
        if (newVal && newVal !== this.model.box_quantity) {
          this.model.box_quantity = newVal;
          // Forzar actualización de la tabla cuando cambie la cantidad de cajas
          this.$nextTick(() => {
            this.$forceUpdate();
          });
        }
      },
      immediate: true,
    },
    compliance_percentage: {
      handler(newVal) {
        this.compliancePercentage = parseFloat(newVal) || 0;

        // Recalcular conflictos con el nuevo porcentaje
        this.$nextTick(() => {
          this.$forceUpdate();
          this.detectConflicts();
        });
      },
      immediate: true,
    },
    box_selected: function (newVal) {
      // El box_selected se usa en getClusterTableColumns y getClusterTableData
      // Recalcular conflictos cuando cambie la caja seleccionada
      this.$nextTick(() => {
        this.detectConflicts();
      });
    },
    date: {
      handler(newVal) {
        // Ya no calculamos porcentaje aquí, por que viene de  Step 1 -> Padre -> Step 5
        this.$nextTick(() => {
          this.detectConflicts();
        });
      },
      immediate: false,
    },
    // Watcher para detectar cambios en los datos que afectan los conflictos
    box_weight: {
      handler() {
        this.$nextTick(() => {
          this.detectConflicts();
        });
      },
      deep: true,
    },
    // Watcher específico para detectar cambios en los pesos de clusters (prop boxes)
    boxes: {
      handler(newVal, oldVal) {
        // Evitar ciclos infinitos
        if (this.isUpdatingConflicts) {
          return;
        }

        // Siempre actualizar conflictos cuando cambien los datos de boxes
        this.$nextTick(() => {
          this.isUpdatingConflicts = true;
          this.detectConflicts();
          this.$forceUpdate();
          // Resetear la bandera después de un pequeño delay
          setTimeout(() => {
            this.isUpdatingConflicts = false;
          }, 100);
        });
      },
      deep: true,
    },
    // Watcher adicional para model.boxes
    'model.boxes': {
      handler(newVal, oldVal) {
        // Evitar ciclos infinitos
        if (this.isUpdatingConflicts) {
          return;
        }

        // Siempre actualizar conflictos cuando cambien los datos de model.boxes
        this.$nextTick(() => {
          this.isUpdatingConflicts = true;
          this.detectConflicts();
          this.$forceUpdate();
          // Resetear la bandera después de un pequeño delay
          setTimeout(() => {
            this.isUpdatingConflicts = false;
          }, 100);
        });
      },
      deep: true,
    },
    sample_quantity: {
      handler() {
        // Evitar ciclos infinitos
        if (this.isUpdatingConflicts) {
          return;
        }

        this.$nextTick(() => {
          this.isUpdatingConflicts = true;
          this.detectConflicts();
          // Forzar actualización de la tabla cuando cambie la cantidad de muestras
          this.$forceUpdate();
          // Resetear la bandera después de un pequeño delay
          setTimeout(() => {
            this.isUpdatingConflicts = false;
          }, 100);
        });
      },
      deep: true,
    },
    // Watchers para detectar cambios en los datos de defectos
    defect_box: {
      handler() {
        this.$nextTick(() => {
          this.detectConflicts();
        });
      },
      deep: true,
    },
    defect_id: {
      handler() {
        this.$nextTick(() => {
          this.detectConflicts();
        });
      },
      deep: true,
    },
    defect_cluster_quantity: {
      handler() {
        this.$nextTick(() => {
          this.detectConflicts();
        });
      },
      deep: true,
    },
    defects: {
      handler() {
        this.$nextTick(() => {
          this.detectConflicts();
        });
      },
      deep: true,
    },
  },
  methods: {
    getMinWeightText() {
      // Obtener el peso mínimo de la caja seleccionada
      const minWeight =
        this.box_selected && this.box_selected.min_weight
          ? parseFloat(this.box_selected.min_weight)
          : null;

      if (minWeight) {
        const weightUnit =
          this.box_selected && this.box_selected.unit_tara_weight
            ? this.box_selected.unit_tara_weight
            : 'kg';

        return ` (Min: ${minWeight.toFixed(2)} ${weightUnit})`;
      }

      return '';
    },

    getMinPCMSText() {
      // Mostrar el porcentaje mínimo de cumplimiento requerido
      if (this.compliancePercentage > 0) {
        return ` (Min: ${this.compliancePercentage}%)`;
      }

      return '';
    },

    getMinUnderweightText() {
      // Mostrar el límite mínimo de gajos bajo peso (actualmente fijo en 2 lb)
      return ' (Min: 2 lb)';
    },

    getClusterTableColumns() {
      // Obtener la unidad de peso de la caja seleccionada
      const weightUnit =
        this.box_selected && this.box_selected.unit_tara_weight
          ? this.box_selected.unit_tara_weight
          : 'kg';

      return [
        {
          prop: 'number_box',
          label: this.$t('production.box'),
          align: 'center',
        },
        {
          label:
            this.$t('common_words.weight') +
            ' (' +
            weightUnit +
            ')' +
            this.getMinWeightText(),
          prop: 'weight',
          align: 'center',
        },
        {
          label: this.$t('boxinspection.pcms') + ' (%)' + this.getMinPCMSText(),
          prop: 'pcms',
          align: 'center',
        },
        {
          label:
            this.$t('boxinspection.total_underweight_fingers') +
            this.getMinUnderweightText(),
          prop: 'total_underweight_fingers',
          align: 'center',
        },
      ];
    },

    getClusterTableData() {
      const data = [];

      let totalPcms = 0;
      let totalUnderweightFingers = 0;

      for (let i = 0; i < this.model.box_quantity; i++) {
        // Usar el peso real del paso 2 - convertir a número
        const weight = parseFloat(this.box_weight[i]) || 0;

        // Calcular PCMS usando la fórmula correcta
        const pcms = this.calculatePCMS(i);

        // Calcular gajos bajo peso basado en datos del paso 4 (< 2lb)
        const underweightFingers = this.calculateUnderweightFingers(i);

        // Acumular totales (peso será calculado de forma ponderada)
        totalPcms += pcms;
        totalUnderweightFingers += underweightFingers;

        data.push({
          number_box: i + 1,
          weight: Math.round(weight * 100) / 100, // Limitar peso a 2 decimales
          pcms: pcms, // Ya limitado en calculatePCMS
          total_underweight_fingers: underweightFingers,
        });
      }

      // Agregar fila de totales
      const averagePcms =
        this.model.box_quantity > 0 ? totalPcms / this.model.box_quantity : 0;

      // Calcular peso total ponderado
      const weightedAverage = this.calculateWeightedTotalWeight();

      data.push({
        number_box: '',
        weight: Math.round(weightedAverage * 100) / 100, // Peso promedio ponderado
        pcms: Math.round(averagePcms * 100) / 100, // Limitar promedio PCMS a 2 decimales
        total_underweight_fingers: totalUnderweightFingers,
        isTotal: true, // Flag para identificar la fila de totales
        isCompliant: this.isCompliant(averagePcms), // Flag para validar cumplimiento
      });

      return data;
    },

    calculatePCMS(boxIndex) {
      // Obtener total de clusters del paso 2
      const totalClusters = this.box_total_clusters[boxIndex] || 0;

      if (totalClusters === 0) {
        return 100; // Si no hay clusters, PCMS es 100%
      }

      // Calcular clusters descontables (defectos con descuento)
      const discountableClusters = this.calculateDiscountableClusters(boxIndex);

      // Aplicar fórmula PCMS: ((Total Clusters - Cant. Cluster descontables) / Total Clusters) * 100
      const pcms =
        ((totalClusters - discountableClusters) / totalClusters) * 100;

      // Limitar a 2 decimales y asegurar que no sea negativo
      return Math.max(0, Math.round(pcms * 100) / 100);
    },

    calculateDiscountableClusters(boxIndex) {
      let discountableClusters = 0;
      const boxNumber = boxIndex + 1; // Número de caja (1-indexed)

      // Recorrer todos los defectos registrados
      for (let i = 0; i < this.defect_box.length; i++) {
        if (this.defect_box[i] === boxNumber) {
          const defectId = this.defect_id[i];
          const clusterQuantity =
            parseInt(this.defect_cluster_quantity[i]) || 0;

          // Verificar si el defecto es descontable
          if (this.isDefectDiscountable(defectId)) {
            discountableClusters += clusterQuantity;
          }
        }
      }

      return discountableClusters;
    },

    isDefectDiscountable(defectId) {
      // Buscar el defecto en la lista de defectos
      const defect = this.defects.find((d) => d.id === defectId);

      // Verificar si el defecto existe y es descontable
      return defect && defect.discountable === true;
    },

    calculateUnderweightFingers(boxIndex) {
      // Obtener los datos de la caja del paso 4
      const boxData = this.boxes[boxIndex] || {};
      const sampleCount = this.sample_quantity[boxIndex] || 0;

      let underweightCount = 0;

      // Si no hay datos del FourthStep (box_3lb es false), retornar 0
      if (!this.box_selected || !this.box_selected.box_3lb) {
        return 0;
      }

      // Recorrer todas las muestras de la caja
      for (let i = 0; i < sampleCount; i++) {
        const sample = boxData[i];
        if (sample && sample.peso) {
          // Verificar si el peso es menor a 2 lb
          if (parseFloat(sample.peso) < 2.0) {
            underweightCount++;
          }
        }
      }

      return underweightCount;
    },

    isCompliant(averagePcms) {
      // Verificar si el promedio de PCMS cumple con el porcentaje requerido
      return averagePcms >= this.compliancePercentage;
    },

    getAnalyticsCellStyle({ row, column, rowIndex, columnIndex }) {
      // Si es la fila de totales, no aplicar color
      const columnProp = column.property;
      if (row.isTotal && columnProp === 'pcms') {
        return this.getPCMSCellStyle(row);
      } else if (row.isTotal) {
        return {};
      }

      // Aplicar colores según la columna
      if (columnProp === 'weight') {
        return this.getWeightCellStyle(row);
      } else if (columnProp === 'pcms') {
        return this.getPCMSCellStyle(row);
      } else if (columnProp === 'total_underweight_fingers') {
        return this.getUnderweightFingersCellStyle(row);
      }

      return {};
    },

    getWeightCellStyle(row) {
      // Obtener el peso mínimo de la caja seleccionada
      const minWeight =
        this.box_selected && this.box_selected.min_weight
          ? parseFloat(this.box_selected.min_weight)
          : 0;

      const currentWeight = parseFloat(row.weight) || 0;

      // Si el peso actual es mayor o igual al mínimo, verde claro, sino rojo claro
      if (currentWeight >= minWeight) {
        return {
          backgroundColor: '#d4edda',
          color: '#155724',
        };
      } else {
        return {
          backgroundColor: '#f8d7da',
          color: '#721c24',
        };
      }
    },

    getPCMSCellStyle(row) {
      const currentPcms = parseFloat(row.pcms) || 0;

      // Si el PCMS es mayor o igual al porcentaje mínimo, verde claro, sino rojo claro
      if (currentPcms >= this.compliancePercentage) {
        return {
          backgroundColor: '#d4edda',
          color: '#155724',
        };
      } else {
        return {
          backgroundColor: '#f8d7da',
          color: '#721c24',
        };
      }
    },

    getUnderweightFingersCellStyle(row) {
      const currentUnderweightFingers =
        parseInt(row.total_underweight_fingers) || 0;

      // Si hay 0 gajos bajo peso, verde claro, sino rojo claro
      if (currentUnderweightFingers === 0) {
        return {
          backgroundColor: '#d4edda',
          color: '#155724',
        };
      } else {
        return {
          backgroundColor: '#f8d7da',
          color: '#721c24',
        };
      }
    },

    detectConflicts() {
      this.conflicts = [];
      this.hasConflicts = false;

      // Evaluación 1: Verificar si todas las cajas están por debajo del peso mínimo
      if (this.checkAllBoxesUnderMinWeight()) {
        this.conflicts.push(
          'Todas las cajas están por debajo del peso mínimo requerido'
        );
        this.hasConflicts = true;
      }

      // Evaluación 2: Verificar si el promedio de PCMS está por debajo del parametrizado
      if (this.checkAveragePCMSBelowCompliance()) {
        this.conflicts.push(
          'El promedio de PCMS está por debajo del porcentaje de cumplimiento requerido'
        );
        this.hasConflicts = true;
      }

      // Evaluación 3: Verificar si hay gajos/clusters bajo peso
      if (this.checkUnderweightClusters()) {
        this.conflicts.push('Hay gajos o clusters con peso inferior a 2 lb');
        this.hasConflicts = true;
      }

      // Emitir conflictos al componente padre
      this.$emit('conflicts-updated', {
        conflicts: [...this.conflicts],
        hasConflicts: this.hasConflicts,
      });

      // Forzar actualización de la UI
      this.$forceUpdate();
    },

    checkAllBoxesUnderMinWeight() {
      if (!this.box_selected || !this.box_selected.min_weight) {
        return false;
      }

      const minWeight = parseFloat(this.box_selected.min_weight);

      for (let i = 0; i < this.model.box_quantity; i++) {
        const weight = parseFloat(this.box_weight[i]) || 0;
        if (weight >= minWeight) {
          return false; // Si al menos una caja cumple el peso mínimo, no hay conflicto
        }
      }

      return true; // Todas las cajas están por debajo del peso mínimo
    },

    checkAveragePCMSBelowCompliance() {
      if (this.compliancePercentage === 0) {
        return false;
      }

      const tableData = this.getClusterTableData();
      const totalRow = tableData.find((row) => row.isTotal);

      if (!totalRow) {
        return false;
      }

      return totalRow.pcms < this.compliancePercentage;
    },

    hasWeightChanges(newVal, oldVal) {
      // Comparar los pesos específicamente para detectar cambios reales
      if (!newVal || !oldVal) return true;

      for (let boxIndex = 0; boxIndex < this.model.box_quantity; boxIndex++) {
        const newBoxData = newVal[boxIndex] || {};
        const oldBoxData = oldVal[boxIndex] || {};
        const sampleCount = this.sample_quantity[boxIndex] || 0;

        for (let i = 0; i < sampleCount; i++) {
          const newSample = newBoxData[i];
          const oldSample = oldBoxData[i];

          if (newSample && oldSample) {
            const newPeso = parseFloat(newSample.peso) || 0;
            const oldPeso = parseFloat(oldSample.peso) || 0;

            if (newPeso !== oldPeso) {
              return true;
            }
          } else if (newSample !== oldSample) {
            return true;
          }
        }
      }

      return false;
    },

    checkUnderweightClusters() {
      // Verificar si hay clusters con peso < 2 lb en los datos del FourthStep
      for (let boxIndex = 0; boxIndex < this.model.box_quantity; boxIndex++) {
        const boxData = this.boxes[boxIndex] || {};
        const sampleCount = this.sample_quantity[boxIndex] || 0;

        for (let i = 0; i < sampleCount; i++) {
          const sample = boxData[i];
          if (sample && sample.peso) {
            if (parseFloat(sample.peso) < 2.0) {
              return true; // Encontró al menos un cluster bajo peso
            }
          }
        }
      }

      return false; // No hay clusters bajo peso
    },

    calculateWeightedTotalWeight() {
      // Calcular peso total ponderado: suma de pesos / cantidad de cajas
      let totalWeight = 0;

      for (let i = 0; i < this.model.box_quantity; i++) {
        const weight = parseFloat(this.box_weight[i]) || 0;
        totalWeight += weight;
      }

      // Retornar el promedio ponderado
      return this.model.box_quantity > 0
        ? totalWeight / this.model.box_quantity
        : 0;
    },

    validate() {
      return this.$validator.validateAll().then((res) => {
        this.$emit('on-validated', this.model);

        return res;
      });
    },
  },
  created() {
    this.showHideLoading(true);
  },
  mounted() {
    if (this.isUpdate() && sessionStorage.getItem('editForm')) {
      const editForm = JSON.parse(sessionStorage.getItem('editForm'));
      const { rejected_boxes } = editForm;
      this.model = {
        ...this.model,
        box_total_rejection: rejected_boxes,
      };
    }

    // Detectar conflictos iniciales
    this.$nextTick(() => {
      this.detectConflicts();
    });
  },
};
</script>

<style scoped>
/* Estilos para la fila de totales */
::v-deep .el-table__body tr:last-child {
  font-weight: bold;
}

::v-deep .el-table__body tr:last-child td {
  font-weight: bold;
  border-top: 2px solid #6ba652;
}

/* Estilos para el texto en negrita en la fila de totales */
::v-deep .el-table__body tr:last-child td strong {
  color: #6ba652;
}
</style>
